import { useState, useEffect, useMemo, useRef, useCallback, useLayoutEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { SlidersHorizontal, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import DateRangeSheet, { formatDateRangeShort } from './ui/DateRangeSheet';
import { supabase } from '../supabaseClient';
import { EventCard } from './EventCard';
import EventFeedResultsGrid from './EventFeedResultsGrid';
import GoingOptionsModal from './rsvp/GoingOptionsModal';
import { useFestivalMatchSetup } from '../hooks/useFestivalMatchSetup';
import RejoinModal from './rsvp/RejoinModal';
import UnRsvpConfirm from './rsvp/UnRsvpConfirm';
import EditRsvpSheet from './rsvp/EditRsvpSheet';
import Modal, { ModalBody, ModalFooter } from './ui/Modal';
import Button from './ui/Button';
import SearchField from './search/SearchField';
import FilterChip from './ui/FilterChip';
import { VenueTierFilter } from './VenueTierFilter';
import { useToast } from '../contexts/ToastContext';
import { appLog } from '../lib/appLog';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { EventListSkeleton, TrendingRailSkeleton } from './EventListSkeleton';
import { isLaunchSplashUp } from './motion/LaunchSplash';
import StaleDataBanner from './ui/StaleDataBanner';

import { resolveEventDisplayName } from '../utils/eventDisplayName';
import { stashPendingAction } from '../lib/pendingAction';
import { openFestivalPicks } from '../lib/festivalPicks';
import {
  useRsvpWrite,
  runRsvpStatusWrite,
  runDeleteRsvpWrite,
  isRsvpMutationQueuedError,
} from '../hooks/mutations/useRsvpWrite';
import { useGroupMembershipWrite } from '../hooks/mutations/useGroupMembershipWrite';
import {
  normalizeRpcEventRow,
  buildEventForCardFromFeedEvent,
  buildFeedQueryFilters,
  defaultRpcMinDateIso,
  filterDisplayOpenEvents,
} from '../utils/eventFeedModel';
import {
  saveEventsFeedScroll,
  restoreEventsFeedScroll,
  attachMainScrollPersistence,
} from '../lib/eventsScrollStorage';
import { useEventsFeed, flattenFeedPages } from '../hooks/queries/useEventsFeed';
import { useTrendingEvents } from '../hooks/queries/useTrendingEvents';
import { useUpcomingFestivals } from '../hooks/queries/useUpcomingFestivals';
import { useCancelledEvents, withoutCancelled } from '../hooks/queries/useCancelledEvents';
import { mergeFestivalsIntoFeed, rankFestivals } from '../utils/festivalPriority';
import { useEventGenres } from '../hooks/queries/useEventGenres';
import { useSearchLog } from '../hooks/useSearchLog';
import { useStayPut } from '../hooks/useStayPut';
import {
  useMyRsvps,
  useMyGoingEvents,
  usePatchMyRsvps,
  useInvalidateMyRsvps,
  myRsvpMap,
} from '../hooks/queries/useMyRsvps';
import { queryKeys } from '../lib/query/queryKeys';
import {
  patchGoingCountInInfiniteFeed,
  patchGoingCountInEventList,
  computeRsvpGoingDelta,
} from '../lib/query/cachePatches';
import { invalidateAfterRsvpChange, afterMatchRpc } from '../lib/query/invalidateMutations';
import { usePromptAuth } from './auth/AuthPrompt';
import { useEventHeadliners } from '../hooks/queries/useEventHeadliners';

function getStartOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function getEndOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}
function getFridayThisWeek() {
  const d = new Date();
  const day = d.getDay();
  const fri = new Date(d);
  fri.setDate(d.getDate() + (5 - day));
  return getStartOfDay(fri);
}
function getSundayThisWeek() {
  const fri = getFridayThisWeek();
  const sun = new Date(fri);
  sun.setDate(fri.getDate() + 2);
  return getEndOfDay(sun);
}

// The quick dates under the search; the Filters sheet shows the same three without "All".
const QUICK_DATES = [
  { key: null, label: 'All' },
  { key: 'today', label: 'Tonight' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'weekend', label: 'This weekend' },
];

// Set to true temporarily to log pagination in production builds.
const __DEBUG_PAGINATION__ = import.meta.env.DEV;

/**
 * The Events feed. Layout keeps it mounted once seen and hides it while another tab is showing (Sam, 2026-10-21: coming
 * back flashed every image while the page was rebuilt), so `active` is false then: it ignores the URL (another page's),
 * saves no scroll, opens no sheet, and restores its scroll when it is shown again.
 */
/**
 * The trending rail on the copy of its first card (or past it): jump back by exactly the copies' offset to the real
 * first card - the same picture, so the jump does not show. `programmaticRef` marks the scroll as ours.
 */
function wrapRail(el, programmaticRef) {
  const copy = el.querySelector('[data-rail-copy]');
  const first = el.firstElementChild;
  if (!copy || !first) return;
  const span = copy.offsetLeft - first.offsetLeft;
  if (el.scrollLeft < span - 2) return;
  programmaticRef.current = Date.now() + 200;
  el.scrollTo({ left: el.scrollLeft - span, behavior: 'instant' });
}

export default function EventsList({ active = true }) {
  const navigate = useNavigate();
  const promptAuth = usePromptAuth();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const { session, authReady } = useCurrentUserProfile();
  const queryClient = useQueryClient();
  const rsvpWrite = useRsvpWrite(session?.user?.id);
  const groupMembershipWrite = useGroupMembershipWrite(session?.user?.id);
  const { maybeInterceptFestivalQueue, festivalSetupModal } = useFestivalMatchSetup(
    session?.user?.id,
  );

  // Filter / UI state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useSearchLog('events', debouncedSearch);
  const [genreFilter, setGenreFilter] = useState('All Genres');
  const [dateFilter, setDateFilter] = useState(null); // null | 'today' | 'tomorrow' | 'weekend' | 'custom'
  const [venueTierFilter, setVenueTierFilter] = useState('all'); // see VENUE_FILTERS for the values
  const [customRange, setCustomRange] = useState([null, null]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [sortBy, setSortBy] = useState('popular'); // 'popular' | 'soon'
  const [showMoreGenres, setShowMoreGenres] = useState(false);
  const [, setRsvpLoadingEventId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filtersSheetEligible, setFiltersSheetEligible] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 639px)').matches : false,
  );
  const [editRsvp, setEditRsvp] = useState({ show: false, eventId: null, source: null });
  const [confirmUnRSVPModal, setConfirmUnRSVPModal] = useState({
    show: false,
    eventId: null,
    groups: [],
    hasQueueEntry: false,
  });
  const [goingOptionsModal, setGoingOptionsModal] = useState({ show: false, eventId: null });
  const [rejoinModal, setRejoinModal] = useState({
    show: false,
    group: null,
    eventId: null,
    skippedGroupIds: [],
    remainingCandidates: [],
  });

  const observerTarget = useRef(null);
  const lastObserverFireRef = useRef(0);
  const trendingScrollRef = useRef(null);
  // The rail's effects follow the element itself, not its card count: a rail that remounts with the same cards (a
  // refetch shows the skeleton while stay-put holds the rows) left the loop's listener on the old one (Sam, 2026-11-01:
  // the copies showed, then it stopped).
  const [railEl, setRailEl] = useState(null);
  const railRef = useCallback((node) => {
    trendingScrollRef.current = node;
    setRailEl(node);
  }, []);
  const searchInputRef = useRef(null);
  const eventsRef = useRef([]);
  const rsvpsRef = useRef([]);
  const loadMoreInFlight = useRef(false);
  const autoPaginateTimeoutRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width: 639px)');
    const sync = () => setFiltersSheetEligible(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const activeRef = useRef(active);
  activeRef.current = active;
  useLayoutEffect(() => {
    if (active) restoreEventsFeedScroll();
  }, [active]);

  useEffect(() => {
    // Checked when the save fires, not when the scroll began: the page taking over the scroll area is not this one.
    return attachMainScrollPersistence(() => {
      if (activeRef.current) saveEventsFeedScroll();
    });
  }, []);

  const { data: genreData } = useEventGenres();
  const primaryGenres = genreData?.primaryGenres ?? ['All Genres'];
  const secondaryGenres = genreData?.secondaryGenres ?? [];

  useEffect(() => {
    if (!showFilters) return;
    if (secondaryGenres.includes(genreFilter)) setShowMoreGenres(true);
  }, [genreFilter, secondaryGenres, showFilters]);

  // Deep link: ?event=... navigates to event page
  useEffect(() => {
    if (!active) return;
    const eventIdParam = searchParams.get('event');
    if (eventIdParam) {
      navigate(`/events/${eventIdParam}`, { replace: true });
    }
  }, [searchParams, navigate, active]);

  const computeRpcMinDate = useCallback(() => {
    if (dateFilter === 'custom' && customRange[0]) {
      return getStartOfDay(customRange[0]).toISOString();
    }
    if (dateFilter === 'today') {
      return getStartOfDay(new Date()).toISOString();
    }
    if (dateFilter === 'tomorrow') {
      const tom = new Date();
      tom.setDate(tom.getDate() + 1);
      return getStartOfDay(tom).toISOString();
    }
    if (dateFilter === 'weekend') {
      return getFridayThisWeek().toISOString();
    }
    return defaultRpcMinDateIso();
  }, [dateFilter, customRange]);

  const computeRpcMaxDate = useCallback(() => {
    if (dateFilter === 'custom' && customRange[0]) {
      // A range picked to its start only is that one day - what the chip says (2026-10-12 audit).
      return getEndOfDay(customRange[1] ?? customRange[0]).toISOString();
    }
    if (dateFilter === 'today') {
      return getEndOfDay(new Date()).toISOString();
    }
    if (dateFilter === 'tomorrow') {
      const tom = new Date();
      tom.setDate(tom.getDate() + 1);
      return getEndOfDay(tom).toISOString();
    }
    if (dateFilter === 'weekend') {
      return getSundayThisWeek().toISOString();
    }
    return null;
  }, [dateFilter, customRange]);

  const feedFilters = useMemo(
    () =>
      buildFeedQueryFilters({
        sortBy,
        genreFilter,
        debouncedSearch,
        venueTierFilter,
        dateFilter,
        customRange,
        userId: session?.user?.id ?? null,
      }),
    [
      sortBy,
      genreFilter,
      debouncedSearch,
      venueTierFilter,
      dateFilter,
      customRange,
      session?.user?.id,
    ],
  );

  const {
    data: feedData,
    isPending: feedPending,
    isFetching: feedFetching,
    isFetchingNextPage,
    isPlaceholderData: feedIsPlaceholder,
    fetchNextPage,
    hasNextPage,
    isError: feedIsError,
    error: feedQueryError,
    dataUpdatedAt: feedUpdatedAt,
  } = useEventsFeed({
    sortBy,
    genreFilter,
    debouncedSearch,
    venueTierFilter,
    dateFilter,
    customRange,
    computeRpcMinDate,
    computeRpcMaxDate,
    userId: session?.user?.id,
    authReady,
  });

  // A cancelled show is not an event anyone can go to. The feed RPCs cannot tell us
  // (they neither filter nor return events.status), so it is filtered here, once, at the
  // top of every list on the page - see api/events.fetchCancelledEventIds.
  const cancelled = useCancelledEvents();
  const events = useMemo(
    () => withoutCancelled(flattenFeedPages(feedData), cancelled),
    [feedData, cancelled],
  );
  const loading = feedPending && events.length === 0 && !feedIsPlaceholder;
  const loadingMore = isFetchingNextPage;
  const hasMore = Boolean(hasNextPage);

  const trendingRailEligible = useMemo(
    () =>
      genreFilter === 'All Genres' &&
      dateFilter == null &&
      venueTierFilter === 'all' &&
      customRange[0] == null &&
      customRange[1] == null &&
      debouncedSearch.trim() === '',
    [genreFilter, dateFilter, venueTierFilter, customRange, debouncedSearch],
  );

  const { data: trendingEvents = [], isPending: trendingPending } = useTrendingEvents({
    userId: session?.user?.id,
    authReady,
    enabled: trendingRailEligible,
  });

  // Festivals lead the list itself — no section of their own (operator, 2026-09-22:
  // "instead of having a new section for festivals ... I just meant prioritize them
  // above anything else"). They cannot simply be sorted out of the loaded feed pages:
  // there were 2,260 upcoming events to 10 festivals and the first one by date was row
  // 214, so they are fetched separately and spliced onto the front.
  //
  // Same eligibility as the trending rail: promoting rows would fight an active filter
  // or search, so it stands down whenever the user is driving the feed.
  const { data: festivals = [] } = useUpcomingFestivals({
    enabled: trendingRailEligible,
  });
  const trendingLoading = trendingPending && trendingEvents.length === 0;

  const { data: rsvps = [] } = useMyRsvps(session?.user?.id, { enabled: authReady });
  const patchMyRsvps = usePatchMyRsvps(session?.user?.id);
  const invalidateMyRsvps = useInvalidateMyRsvps();

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  useEffect(() => {
    rsvpsRef.current = rsvps;
  }, [rsvps]);

  const loadMore = useCallback(async () => {
    if (!hasNextPage || isFetchingNextPage || loading || loadMoreInFlight.current) return;
    loadMoreInFlight.current = true;
    try {
      await fetchNextPage();
    } catch (err) {
      showToast(err.message || 'Could not load more events.');
    } finally {
      loadMoreInFlight.current = false;
    }
  }, [hasNextPage, isFetchingNextPage, loading, fetchNextPage, showToast]);

  // The carousel is untouched: a festival people are going to belongs in it, exactly as
  // before (operator, 2026-09-22: "i'm seeing portola now not in the featured carousel
  // when it has high rsvps and it's a festival"). It is only kept out of the list below,
  // the same way every trending event always has been.
  const railEvents = useMemo(
    () => withoutCancelled(trendingEvents, cancelled),
    [trendingEvents, cancelled],
  );

  const trendingIds = useMemo(() => new Set(railEvents.map((e) => e.event_id)), [railEvents]);

  // rankFestivals also drops the club-tier ones - see utils/festivalPriority. A festival
  // already in the carousel is not repeated in the list, which is the rule the feed has
  // always followed for trending events.
  const leadFestivals = useMemo(
    () =>
      trendingRailEligible
        ? rankFestivals(withoutCancelled(filterDisplayOpenEvents(festivals), cancelled)).filter(
            (e) => !trendingIds.has(e.event_id),
          )
        : [],
    [festivals, trendingRailEligible, trendingIds, cancelled],
  );

  const festivalIds = useMemo(() => new Set(leadFestivals.map((e) => e.event_id)), [leadFestivals]);

  const visibleFeedEvents = useMemo(() => {
    if (!trendingRailEligible) return events;
    if (railEvents.length === 0 && festivalIds.size === 0) return events;
    return events.filter((e) => !trendingIds.has(e.event_id) && !festivalIds.has(e.event_id));
  }, [events, trendingIds, festivalIds, trendingRailEligible, railEvents]);

  // page: the desktop arrows move a screenful of cards (less one, so the last one in view leads the next), not one.
  const scrollTrendingBy = useCallback((direction, page = false) => {
    const el = trendingScrollRef.current;
    if (!el) return;
    const firstCard = el.firstElementChild;
    if (!firstCard) return;
    const cardWidth = firstCard.getBoundingClientRect().width;
    const gap = 12; // matches gap-3
    const step = cardWidth + gap;
    const cards = page ? Math.max(1, Math.floor(el.clientWidth / step) - 1) : 1;
    el.scrollBy({ left: direction * cards * step, behavior: 'smooth' });
  }, []);
  const [railEnds, setRailEnds] = useState({ start: true, end: false });

  // The rail drifts one card to the right every few seconds so it's obvious
  // there's more; wraps to the start at the end. A horizontal interaction -
  // the viewer scrolling the rail themselves or a wheel - holds it
  // for a while. A vertical page scroll that happens to start on a card does
  // not count. The tab being hidden, the rail being off-screen, or reduced
  // motion stop it entirely.
  const trendingTouchedAtRef = useRef(0);
  const trendingProgrammaticUntilRef = useRef(0);
  const noteTrendingTouch = useCallback(() => {
    trendingTouchedAtRef.current = Date.now();
  }, []);
  // Desktop browser: the rail scrolls by mouse too - drag it, or a shift-wheel / sideways wheel. The wheel listener is
  // attached by hand because React's onWheel is passive and cannot preventDefault (the page would scroll instead).
  // A drag suppresses the click that would otherwise open the card under the pointer.
  const railDragRef = useRef(null);
  useEffect(() => {
    const el = railEl;
    if (!el) return undefined;
    const onWheel = (e) => {
      const sideways =
        Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.shiftKey ? e.deltaY : 0;
      if (!sideways) return;
      e.preventDefault();
      el.scrollLeft += sideways;
      noteTrendingTouch();
    };
    const onDown = (e) => {
      if (e.button !== 0 || e.pointerType === 'touch') return;
      railDragRef.current = { x: e.clientX, left: el.scrollLeft, moved: false, id: e.pointerId };
      el.classList.add('trending-rail-scroll--dragging');
    };
    const onMove = (e) => {
      const d = railDragRef.current;
      if (!d || d.id !== e.pointerId) return;
      const dx = e.clientX - d.x;
      if (!d.moved && Math.abs(dx) > 4) {
        d.moved = true;
        try {
          el.setPointerCapture(e.pointerId);
        } catch {
          /* capture is a nicety */
        }
      }
      if (d.moved) {
        el.scrollLeft = d.left - dx;
        noteTrendingTouch();
      }
    };
    const onUp = (e) => {
      const d = railDragRef.current;
      if (!d) return;
      railDragRef.current = null;
      el.classList.remove('trending-rail-scroll--dragging');
      if (d.moved) {
        const swallow = (ev) => {
          ev.stopPropagation();
          ev.preventDefault();
          el.removeEventListener('click', swallow, true);
        };
        el.addEventListener('click', swallow, true);
        setTimeout(() => el.removeEventListener('click', swallow, true), 0);
      }
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* not captured */
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
    };
  }, [noteTrendingTouch, railEl]);
  useEffect(() => {
    const el = railEl;
    if (!el || !trendingRailEligible || railEvents.length < 2) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;

    // Counted in time the rail has actually been SEEN (Sam, 2026-10-21: on first open it had already moved on to the
    // second event before the page was visible). Mount time is not it: the launch splash, the first-run intro and a
    // backgrounded app all cover a mounted rail, and the observer only reports once it has measured.
    const FIRST_MS = 5000;
    const ADVANCE_MS = 5000;
    const HOLD_AFTER_TOUCH_MS = 9000;
    const SMOOTH_SCROLL_MS = 1200; // scroll events inside this window after our own scrollBy are ours
    const STEP_MS = 250;
    let onScreen = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        onScreen = entry.isIntersecting;
      },
      { threshold: 0.6 },
    );
    io.observe(el);

    const onScroll = () => {
      if (Date.now() > trendingProgrammaticUntilRef.current) noteTrendingTouch();
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('wheel', noteTrendingTouch, { passive: true });
    // A held finger too: the rail used to slide a card out from under a long-press (2026-10-04).
    el.addEventListener('touchstart', noteTrendingTouch, { passive: true });

    let seenMs = 0; // seen, uncovered, since the last move (or since it mounted)
    let due = FIRST_MS;
    let timer;
    const tick = () => {
      timer = setTimeout(tick, STEP_MS);
      if (document.hidden || !onScreen || isLaunchSplashUp()) return;
      if (Date.now() - trendingTouchedAtRef.current < HOLD_AFTER_TOUCH_MS) {
        seenMs = 0;
        due = ADVANCE_MS;
        return;
      }
      seenMs += STEP_MS;
      if (seenMs < due) return;
      seenMs = 0;
      due = ADVANCE_MS;
      // on the copy of the first card: back to the real one before moving on, whether or not a scroll event said so
      wrapRail(el, trendingProgrammaticUntilRef);
      trendingProgrammaticUntilRef.current = Date.now() + SMOOTH_SCROLL_MS;
      scrollTrendingBy(1); // always forward: the loop's copies (railLoop) carry it round to the first card again
    };
    timer = setTimeout(tick, STEP_MS);

    return () => {
      clearTimeout(timer);
      io.disconnect();
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('wheel', noteTrendingTouch);
      el.removeEventListener('touchstart', noteTrendingTouch);
    };
  }, [railEl, trendingRailEligible, railEvents.length, scrollTrendingBy, noteTrendingTouch]);

  const userRsvpByEvent = useMemo(() => myRsvpMap(rsvps), [rsvps]);
  const lastEventElementRef = useCallback(
    (node) => {
      if (loadingMore || !hasMore) return;
      if (observerTarget.current) observerTarget.current.disconnect();
      observerTarget.current = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        const now = Date.now();
        if (now - lastObserverFireRef.current < 600) return;
        lastObserverFireRef.current = now;
        if (__DEBUG_PAGINATION__) {
          console.log('[observer] intersecting, triggering loadMore', { timestamp: now });
        }
        loadMore();
      });
      if (node) observerTarget.current.observe(node);
    },
    [loadingMore, hasMore, loadMore],
  );

  useEffect(() => {
    return () => {
      if (observerTarget.current) observerTarget.current.disconnect();
    };
  }, []);

  // What the FEED itself matched, after the client-side display cutoff. The pagination
  // gates below all ask about THIS, never the rendered list: page one is mostly events
  // that have already happened (the RPC looks back 12 days and sorts by going_count, so
  // a popular past show outranks a quiet future one), and the effect that chases more
  // pages "until something matches" has to keep seeing zero. Merging the festivals into
  // the list it watched stopped that loop dead - the festivals are always there, so the
  // feed never loaded a second page and no concert ever appeared (2026-09-22).
  const feedMatches = useMemo(
    () => filterDisplayOpenEvents(visibleFeedEvents),
    [visibleFeedEvents],
  );

  // Festivals sit IN the list, not above it: merged on RSVP popularity first, so a
  // festival nobody has RSVP'd to lands below the hero carousel's popular events
  // (operator, 2026-09-22: "i literally just want these events to be at the top of the
  // list (minus popular events that have a lot of rsvps)").
  // Your events lead the list, right under the carousel, soonest first, no heading (Sam, 2026-10-28). Fetched by id
  // like the festivals: a show months out is not in the loaded pages. One in the carousel stays there only.
  const { data: myGoingEvents = [] } = useMyGoingEvents(rsvps, { enabled: trendingRailEligible });
  const leadMine = useMemo(
    () =>
      trendingRailEligible
        ? withoutCancelled(myGoingEvents, cancelled)
            .filter((e) => !trendingIds.has(e.event_id))
            .map(normalizeRpcEventRow)
        : [],
    [myGoingEvents, trendingRailEligible, trendingIds, cancelled],
  );
  const filteredEvents = useMemo(() => {
    const merged = mergeFestivalsIntoFeed(feedMatches, leadFestivals);
    if (leadMine.length === 0) return merged;
    const mineIds = new Set(leadMine.map((e) => e.event_id));
    return [...leadMine, ...merged.filter((e) => !mineIds.has(e.event_id))];
  }, [feedMatches, leadFestivals, leadMine]);

  // After you Join or leave something here, nothing moves until you leave the page or change a filter (useStayPut).
  const stayPutKey = JSON.stringify([
    genreFilter,
    dateFilter,
    venueTierFilter,
    customRange,
    debouncedSearch,
  ]);
  const [stayPutFor, setStayPutFor] = useState(null);
  const [railShown, listShown] = useStayPut(
    [railEvents, filteredEvents],
    stayPutFor === stayPutKey,
  );
  // The rail goes round, not back (Sam, 2026-10-31: at the end it scrolled all the way back to the first card). The first two
  // cards follow the last as copies; once a move or a swipe settles on the copy of the first, the rail jumps by exactly
  // the copies' offset to the real first card - the same picture, so the jump does not show - and carries on forward.
  // Two copies, not one: the last card cannot snap to the left edge (no room after it), the copy of the first can.
  const railLoop = railShown.length >= 2;
  // The trending cards' play button plays the headliners.
  const { data: railHeadliners = {} } = useEventHeadliners(railShown.map((e) => e.event_id));
  useEffect(() => {
    const el = railEl;
    if (!el || !railLoop) return undefined;
    let idle = 0;
    const wrap = () => wrapRail(el, trendingProgrammaticUntilRef);
    const onScroll = () => {
      clearTimeout(idle);
      idle = setTimeout(wrap, 160);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(idle);
      el.removeEventListener('scroll', onScroll);
    };
  }, [railEl, railLoop]);

  const isInitialFeedLoading = loading && events.length === 0;

  const isAutoPaginationChasing = useMemo(
    () => !loading && events.length > 0 && feedMatches.length === 0 && (hasMore || loadingMore),
    [loading, events.length, feedMatches.length, hasMore, loadingMore],
  );

  const showFeedExhaustedEmpty = useMemo(
    () =>
      !loading &&
      !loadingMore &&
      !hasMore &&
      feedMatches.length === 0 &&
      filteredEvents.length === 0,
    [loading, loadingMore, hasMore, feedMatches.length, filteredEvents.length],
  );

  // If client-side filters hide every loaded row, fetch more pages until something matches or the server runs out
  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;
    if (loadMoreInFlight.current) return;
    if (feedMatches.length > 0) return;
    if (events.length === 0) return;

    if (__DEBUG_PAGINATION__) {
      console.log('[auto-pagination] scheduling loadMore', {
        loading,
        loadingMore,
        hasMore,
        filteredCount: feedMatches.length,
        eventsCount: events.length,
        timestamp: Date.now(),
      });
    }
    // Throttle back-to-back auto-pagination so layout has time to settle
    autoPaginateTimeoutRef.current = setTimeout(() => {
      if (__DEBUG_PAGINATION__) {
        console.log('[auto-pagination] firing loadMore after 200ms', { timestamp: Date.now() });
      }
      loadMore();
    }, 200);

    return () => {
      if (autoPaginateTimeoutRef.current) {
        clearTimeout(autoPaginateTimeoutRef.current);
        autoPaginateTimeoutRef.current = null;
      }
    };
  }, [loading, loadingMore, hasMore, feedMatches.length, events.length, loadMore]);

  useEffect(() => {
    return () => {
      if (autoPaginateTimeoutRef.current) {
        clearTimeout(autoPaginateTimeoutRef.current);
      }
    };
  }, []);

  const refetchRsvps = async () => {
    invalidateMyRsvps(session?.user?.id);
    invalidateAfterRsvpChange(queryClient, { userId: session?.user?.id });
  };

  const updateLocalRsvp = (eventId, newStatus) => {
    const oldR = rsvpsRef.current.find((r) => r.event_id === eventId);
    const oldStatus = oldR?.status;
    const delta = computeRsvpGoingDelta(oldStatus, newStatus);

    patchMyRsvps(eventId, newStatus);

    if (delta !== 0) {
      patchGoingCountInInfiniteFeed(
        queryClient,
        queryKeys.events.feed(feedFilters),
        eventId,
        delta,
      );
      patchGoingCountInEventList(
        queryClient,
        queryKeys.events.trending(session?.user?.id),
        eventId,
        delta,
      );
    }
  };

  // Helper function to get toast message based on event timing
  const getRsvpToastMessage = () => {
    return "You're looking for a group! We'll match you as soon as others join.";
  };

  const getEventForCutoff = async (eventId) => {
    const fromFeed = events.find((event) => event.event_id === eventId);
    if (fromFeed) return fromFeed;
    const fromTrending =
      trendingEvents.find((event) => event.event_id === eventId) ??
      leadFestivals.find((event) => event.event_id === eventId);
    if (fromTrending) return fromTrending;

    const { data } = await supabase
      .from('events')
      .select('event_id, event_datetime, event_date, venues(venue_tier)')
      .eq('event_id', eventId)
      .maybeSingle();
    return data || null;
  };

  const handleRejoinGroup = async () => {
    const { group, eventId, skippedGroupIds } = rejoinModal;
    if (!group || !eventId) return;
    setRejoinModal({
      show: false,
      group: null,
      eventId: null,
      skippedGroupIds: [],
      remainingCandidates: [],
    });

    const { data, error } = await supabase.rpc('confirm_rejoin_group', {
      p_group_id: group.group_id,
    });
    if (error) {
      console.error('[EventsList] confirm_rejoin_group error:', error);
      showToast('Failed to rejoin group.');
      return;
    }
    afterMatchRpc(queryClient, { userId: session?.user?.id, eventId, groupId: data?.group_id }); // the RPC is the RSVP here: refresh Maps' list and start the map
    if (data.outcome === 'rejoined') {
      showToast("You've rejoined the group!");
      navigate(`/groups/${data.group_id}`);
    } else if (data.outcome === 'full') {
      showToast('This group is now full. Finding another group...');
      const skipIds = [...(skippedGroupIds || []), group.group_id];
      const { data: matchData, error: matchErr } = await supabase.rpc('match_user_to_group', {
        p_event_id: eventId,
        p_skip_group_ids: skipIds,
      });
      if (matchErr) {
        showToast('Could not find a group.');
        return;
      }
      afterMatchRpc(queryClient, {
        userId: session?.user?.id,
        eventId,
        groupId: matchData?.group_id,
      }); // the RPC is the RSVP here: refresh Maps' list and start the map
      if (matchData.outcome === 'matched' || matchData.outcome === 'already_member') {
        showToast("You're matched into a group.");
        navigate(`/groups/${matchData.group_id}`);
      } else if (matchData.outcome === 'queued') {
        showToast(getRsvpToastMessage());
      } else if (matchData.outcome === 'rejoin_available' && matchData.candidates?.length) {
        setRejoinModal({
          show: true,
          group: matchData.candidates[0],
          eventId,
          skippedGroupIds: skipIds,
          remainingCandidates: matchData.candidates.slice(1),
        });
      } else if (matchData.outcome === 'closed') {
        showToast('Matching is closed for this event.');
      } else if (matchData.outcome === 'error') {
        showToast(matchData.message || 'Could not find a group.');
      }
    } else {
      showToast(data.message || 'Could not rejoin group.');
    }
  };

  const handleSkipRejoinGroup = async () => {
    const { group, eventId, skippedGroupIds, remainingCandidates } = rejoinModal;
    if (!group || !eventId) return;

    const newSkippedIds = [...skippedGroupIds, group.group_id];

    const nextCandidate = remainingCandidates.find((c) => !newSkippedIds.includes(c.group_id));

    if (nextCandidate) {
      setRejoinModal({
        show: true,
        group: nextCandidate,
        eventId,
        skippedGroupIds: newSkippedIds,
        remainingCandidates: remainingCandidates.filter(
          (c) => c.group_id !== nextCandidate.group_id,
        ),
      });
      return;
    }

    setRejoinModal({
      show: false,
      group: null,
      eventId: null,
      skippedGroupIds: [],
      remainingCandidates: [],
    });

    const { data, error } = await supabase.rpc('match_user_to_group', {
      p_event_id: eventId,
      p_skip_group_ids: newSkippedIds,
    });
    if (error) {
      showToast('Could not find a group.');
      return;
    }
    afterMatchRpc(queryClient, { userId: session?.user?.id, eventId, groupId: data?.group_id }); // the RPC is the RSVP here: refresh Maps' list and start the map

    if (data.outcome === 'matched' || data.outcome === 'already_member') {
      showToast("You're matched into a group.");
      navigate(`/groups/${data.group_id}`);
    } else if (data.outcome === 'queued') {
      showToast(getRsvpToastMessage());
    } else if (data.outcome === 'closed') {
      showToast('Matching is closed for this event.');
    } else if (data.outcome === 'rejoin_available' && data.candidates?.length) {
      setRejoinModal({
        show: true,
        group: data.candidates[0],
        eventId,
        skippedGroupIds: newSkippedIds,
        remainingCandidates: data.candidates.slice(1),
      });
    }
  };

  const setRsvpStatusForEvent = async (
    eventId,
    status,
    goingChoice = 'queue',
    previousStatus = null,
    groupName = null,
  ) => {
    appLog('debug', 'join.start', { eventId, status, goingChoice, source: 'feed' });
    try {
      const result = await runRsvpStatusWrite({
        rsvpWrite,
        userId: session.user.id,
        eventId,
        status,
        goingChoice,
        previousStatus,
        groupName,
        getEventForCutoff,
        showToast,
        navigate,
        setRejoinModal,
        getRsvpToastMessage,
      });
      appLog('debug', 'join.done', { eventId, status, goingChoice, source: 'feed' });
      return result;
    } catch (err) {
      console.error('[Join] failed:', {
        eventId,
        status,
        goingChoice,
        name: err?.name,
        message: err?.message,
        stack: err?.stack,
        fullError: err,
      });
      throw err;
    }
  };

  const deleteRsvpForEvent = async (eventId, previousStatus = null) =>
    runDeleteRsvpWrite({
      rsvpWrite,
      userId: session.user.id,
      eventId,
      previousStatus,
    });

  const checkGroupsBeforeUnRSVP = async (eventId) => {
    // Check if user is in any groups for this event
    const { data: userGroupsData, error: groupsError } = await supabase
      .from('group_members')
      .select(
        `
        group_id,
        groups!inner(
          id,
          name,
          event_id
        )
      `,
      )
      .eq('user_id', session.user.id)
      .eq('groups.event_id', eventId)
      .is('left_at', null);

    if (groupsError) {
      console.error('[EventsList] Error checking groups:', groupsError);
      // Continue to check queue even if groups check fails
    }

    // Check if user is in the matching queue (pending or matched)
    const { data: queueData, error: queueError } = await supabase
      .from('group_matching_queue')
      .select('id, status')
      .eq('user_id', session.user.id)
      .eq('event_id', eventId);

    if (queueError) {
      console.error('[EventsList] Error checking queue:', queueError);
    }

    const hasGroups = userGroupsData && userGroupsData.length > 0;
    const hasQueueEntry = queueData && queueData.length > 0;

    // Show confirmation if user is in groups OR in queue
    if (hasGroups || hasQueueEntry) {
      setConfirmUnRSVPModal({
        show: true,
        eventId: eventId,
        groups: userGroupsData || [],
        hasQueueEntry: hasQueueEntry,
      });
      return true; // Don't proceed with un-RSVP yet
    }

    return false; // No groups or queue entries, proceed normally
  };

  const handleConfirmUnRSVP = async () => {
    const { eventId, groups } = confirmUnRSVPModal;

    try {
      // Get user profile for system messages
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', session.user.id)
        .single();

      const displayName = userProfile?.display_name || 'Someone';

      // Append voluntary leave rows, system messages, creator handoff, archive if empty
      if (groups.length > 0) {
        for (const g of groups) {
          await groupMembershipWrite.mutateAsync({
            action: 'leave',
            groupId: g.group_id,
            userId: session.user.id,
            displayName,
            groupName: g.groups?.name,
            postSystemMessage: true,
          });
        }
      }

      await deleteRsvpForEvent(eventId, 'going');

      // Update local RSVP state
      updateLocalRsvp(eventId, null);

      // Close modal
      setConfirmUnRSVPModal({ show: false, eventId: null, groups: [], hasQueueEntry: false });

      // Show toast
      showToast("You're no longer marked as going.");

      // Refresh RSVPs
      await refetchRsvps();
    } catch (err) {
      console.error('[EventsList] Error confirming un-RSVP:', err);
    }
  };

  const handleCardGoing = async (eventId, myStatus, e, source = 'unknown', confirmed = false) => {
    e.stopPropagation();
    if (!session) {
      stashPendingAction({ type: 'rsvp_going', eventId });
      // The sign-in pop-up over the feed (Stage 8); signed in, the event page finishes the stashed RSVP.
      const ev = await getEventForCutoff(eventId).catch(() => null);
      promptAuth({
        action: 'Join',
        target: resolveEventDisplayName(ev) || 'this event',
        onSuccess: () => navigate(`/events/${eventId}`),
      });
      return;
    }
    // Going opens Edit RSVP first (Sam's B, 2026-11-04); its Not going comes back here confirmed.
    if (myStatus === 'going' && !confirmed) {
      setEditRsvp({ show: true, eventId, source });
      return;
    }
    setStayPutFor(stayPutKey);
    setRsvpLoadingEventId(eventId);
    try {
      if (myStatus === 'going') {
        // User is removing their RSVP - check for groups first
        const hasGroups = await checkGroupsBeforeUnRSVP(eventId);
        if (hasGroups) {
          // Modal will handle the un-RSVP
          setRsvpLoadingEventId(null);
          return;
        }
        // No groups, proceed normally
        localStorage.removeItem(`dismissed_group_prompt_${eventId}`);
        await deleteRsvpForEvent(eventId);
        updateLocalRsvp(eventId, null);
        showToast("You're no longer marked as going.");
      } else {
        setGoingOptionsModal({ show: true, eventId });
        return;
      }
    } catch (err) {
      console.error('[Join] handleCardGoing failed:', {
        eventId,
        source,
        name: err?.name,
        message: err?.message,
        stack: err?.stack,
        fullError: err,
      });
    } finally {
      setRsvpLoadingEventId(null);
    }
  };

  const closeGoingOptionsModal = () => {
    setGoingOptionsModal({ show: false, eventId: null });
  };

  const handleGoingOption = async (choice, groupName = null) => {
    const eventId = goingOptionsModal.eventId;
    if (!eventId) return;
    setGoingOptionsModal({ show: false, eventId: null });

    // Festivals collect attendance days + lineup votes before queueing.
    if (
      choice === 'queue' &&
      (await maybeInterceptFestivalQueue(eventId, () => executeGoingOption(eventId, choice)))
    ) {
      return;
    }

    await executeGoingOption(eventId, choice, groupName);
  };

  const executeGoingOption = async (eventId, choice, groupName = null) => {
    setRsvpLoadingEventId(eventId);
    try {
      updateLocalRsvp(eventId, 'going');
      if (choice === 'skip') {
        showToast("You're going");
      }
      const toastMessage = await setRsvpStatusForEvent(eventId, 'going', choice, null, groupName);
      if (toastMessage) {
        showToast(toastMessage);
      }
      // Just me on a festival: its picks come next (a new group opens the group; the queue took picks in its modal).
      if (choice === 'skip') void openFestivalPicks({ queryClient, navigate, eventId });
    } catch (err) {
      if (isRsvpMutationQueuedError(err)) {
        showToast(err.message);
        return;
      }
      console.error('[Join] handleGoingOption failed:', {
        eventId,
        choice,
        name: err?.name,
        message: err?.message,
        stack: err?.stack,
        fullError: err,
      });
    } finally {
      setRsvpLoadingEventId(null);
      setGoingOptionsModal({ show: false, eventId: null });
    }
  };

  const handleJoinExistingGroup = async (groupId) => {
    const eventId = goingOptionsModal.eventId;
    if (!eventId || !groupId) return;
    setGoingOptionsModal({ show: false, eventId: null });
    setRsvpLoadingEventId(eventId);
    try {
      await setRsvpStatusForEvent(eventId, 'going', 'skip');
      updateLocalRsvp(eventId, 'going');
      stashPendingAction({ type: 'join_group_chat', groupId });
      navigate(`/groups/${groupId}`);
    } catch (err) {
      console.error('[Join] handleJoinExistingGroup failed:', {
        eventId,
        groupId,
        name: err?.name,
        message: err?.message,
        stack: err?.stack,
        fullError: err,
      });
      showToast(err?.message || 'Could not join group.');
    } finally {
      setRsvpLoadingEventId(null);
    }
  };

  const feedBlockingError = feedIsError && events.length === 0 && !feedPending;
  const showFeedStaleBanner = events.length > 0 && feedIsError;
  const showFeedFilterBanner =
    feedIsPlaceholder && feedFetching && !isFetchingNextPage && events.length > 0;

  if (feedBlockingError) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="rounded-lg bg-danger-fill text-danger p-4">
          {feedQueryError?.message || 'Failed to load events.'}
        </div>
      </div>
    );
  }

  // A custom date filter counts only once a range is picked. Dates are always on show under the search (quick chips and
  // the calendar chip), so the Filters button lights only for what the sheet alone shows: sort, genre and type.
  const dateFilterOn = dateFilter != null && (dateFilter !== 'custom' || Boolean(customRange[0]));
  const sheetFiltersCount =
    (venueTierFilter !== 'all' ? 1 : 0) +
    (genreFilter !== 'All Genres' ? 1 : 0) +
    (sortBy !== 'popular' ? 1 : 0);
  const isResettable = sheetFiltersCount > 0 || dateFilterOn;
  const customRangeOn = dateFilter === 'custom' && Boolean(customRange[0]);

  // Any date chip replaces a picked range (a stale range kept Trending hidden and the feed off its cached key).
  const pickQuickDate = (key) => {
    setDateFilter(key);
    setCustomRange([null, null]);
  };
  const resetFilters = () => {
    setVenueTierFilter('all');
    setGenreFilter('All Genres');
    setSortBy('popular');
    setDateFilter(null);
    setCustomRange([null, null]);
  };

  // The calendar chip, the same on the page and in the Filters sheet: opens "Pick dates", reads the dates once picked.
  const calendarChip = (
    <FilterChip
      pill
      selected={customRangeOn}
      aria-pressed={customRangeOn}
      aria-label={
        customRangeOn
          ? `Dates: ${formatDateRangeShort(customRange[0], customRange[1])}`
          : 'Pick dates'
      }
      onClick={() => setCalendarOpen(true)}
    >
      <span className="inline-flex items-center gap-1.5">
        <CalendarDays className="h-4 w-4" aria-hidden />
        {customRangeOn ? formatDateRangeShort(customRange[0], customRange[1]) : null}
      </span>
    </FilterChip>
  );

  const filterSectionTitle =
    'mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-dark-muted';

  // One set of controls for the desktop panel and the phone sheet (they used to differ: selects on desktop, chips on
  // the phone, and a react-datepicker in both).
  // The desktop panel leaves Date out: the quick dates and the calendar chip sit right above it (Sam, 2026-10-13).
  const filterControls = (withDates) => (
    <div className="space-y-5">
      {withDates && (
        <section aria-label="Date">
          <h3 className={filterSectionTitle}>Date</h3>
          <div className="flex flex-wrap gap-1.5">
            {calendarChip}
            {QUICK_DATES.filter(({ key }) => key).map(({ key, label }) => (
              <FilterChip
                key={key}
                pill
                selected={dateFilter === key}
                aria-pressed={dateFilter === key}
                onClick={() => pickQuickDate(dateFilter === key ? null : key)}
              >
                {label}
              </FilterChip>
            ))}
          </div>
        </section>
      )}

      <section aria-label="Sort">
        <h3 className={filterSectionTitle}>Sort</h3>
        <div className="flex flex-wrap gap-1.5">
          {[
            { value: 'popular', label: 'Popular' },
            { value: 'soon', label: 'Date' },
          ].map((option) => (
            <FilterChip
              key={option.value}
              pill
              selected={sortBy === option.value}
              aria-pressed={sortBy === option.value}
              onClick={() => setSortBy(option.value)}
            >
              {option.label}
            </FilterChip>
          ))}
        </div>
      </section>

      <section aria-label="Genre">
        <h3 className={filterSectionTitle}>Genre</h3>
        <div className="flex flex-wrap gap-1.5">
          {[...primaryGenres, ...(showMoreGenres ? secondaryGenres : [])].map((genre) => (
            <FilterChip
              key={genre}
              pill
              selected={genreFilter === genre}
              aria-pressed={genreFilter === genre}
              onClick={() => setGenreFilter(genre)}
            >
              {genre === 'All Genres' ? 'All' : genre}
            </FilterChip>
          ))}
          {secondaryGenres.length > 0 && (
            <FilterChip
              pill
              onClick={() => setShowMoreGenres((v) => !v)}
              aria-expanded={showMoreGenres}
            >
              {showMoreGenres ? 'Less' : `${secondaryGenres.length} more`}
            </FilterChip>
          )}
        </div>
      </section>

      <section aria-label="Type">
        <h3 className={filterSectionTitle}>Type</h3>
        <VenueTierFilter
          pill
          selectedFilter={venueTierFilter}
          onFilterChange={setVenueTierFilter}
        />
      </section>
    </div>
  );

  return (
    <div className="pt-4 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* The tab dock says where you are; the search bar is the first thing on the page. */}
        <h1 className="sr-only">Events</h1>

        <StaleDataBanner
          visible={showFeedStaleBanner}
          dataUpdatedAt={feedUpdatedAt}
          isFetching={feedFetching}
          className="mb-4"
        />

        {/* Search bar — desktop inline; mobile opens /events/search route. Filters is a round icon button at its right
          end rather than a full-width bar under it (operator, 2026-09-28); accent while any filter is on. */}
        <div className="flex items-center gap-2 mb-4">
          <SearchField
            ref={searchInputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Artists, venues, events"
            onClear={() => {
              setSearch('');
              searchInputRef.current?.focus();
            }}
            className="hidden sm:flex min-w-0 flex-1"
          />

          <SearchField
            readOnly
            placeholder="Artists, venues, events"
            onActivate={() => {
              saveEventsFeedScroll();
              navigate('/events/search');
            }}
            aria-label="Search events"
            className="sm:hidden min-w-0 flex-1"
          />

          <Button
            type="button"
            variant="icon"
            selected={showFilters || sheetFiltersCount > 0}
            onClick={() => setShowFilters(!showFilters)}
            aria-label={sheetFiltersCount > 0 ? `Filters (${sheetFiltersCount} on)` : 'Filters'}
            aria-expanded={showFilters}
            className="!h-11 !w-11"
          >
            <SlidersHorizontal className="w-[18px] h-[18px] shrink-0" aria-hidden />
          </Button>
        </div>

        {/* Quick dates, always on show under the search (operator, 2026-09-29), and the calendar chip for any day or
          range (Sam, 2026-10-12): the same dateFilter the Filters sheet sets. Sideways only: overflow-x alone makes the
          row a vertical scroller too, and a drag dragged the chips up and down inside it (Sam, 2026-10-16). py-1 keeps
          the selected chip's glow inside the clip. Not touch-action: pan-x, which blocks the page scroll from here. */}
        <div
          className="-mx-4 mb-3 flex gap-1.5 overflow-x-auto overflow-y-hidden overscroll-x-contain px-4 py-1 scrollbar-hide sm:mx-0 sm:px-0"
          role="group"
          aria-label="When"
        >
          {/* All, then the calendar, then the quick dates (Sam, 2026-10-21). */}
          {QUICK_DATES.map(({ key, label }) => [
            <FilterChip
              key={label}
              pill
              selected={dateFilter === key}
              aria-pressed={dateFilter === key}
              onClick={() => pickQuickDate(key)}
            >
              {label}
            </FilterChip>,
            key === null && (
              <span key="calendar" className="contents">
                {calendarChip}
              </span>
            ),
          ])}
        </div>
        {/* Opened from the page or from the Filters sheet; it stacks over the sheet, and Done returns to it. */}
        <DateRangeSheet
          isOpen={active && calendarOpen}
          onClose={() => setCalendarOpen(false)}
          value={customRangeOn ? customRange : [null, null]}
          onApply={([start, end]) => {
            if (start) {
              setCustomRange([start, end]);
              setDateFilter('custom');
            } else {
              setCustomRange([null, null]);
              if (dateFilter === 'custom') setDateFilter(null);
            }
          }}
        />

        {/* Desktop: expandable filters panel */}
        {showFilters && (
          <div className="hidden sm:block mb-6 rounded-2xl border border-gray-200 p-4 dark:border-white/10">
            {filterControls(false)}
            <div className="mt-5 flex justify-start">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={resetFilters}
                disabled={!isResettable}
              >
                Reset
              </Button>
            </div>
          </div>
        )}

        {/* Mobile: filters bottom sheet */}
        <Modal
          isOpen={active && showFilters && filtersSheetEligible}
          onClose={() => setShowFilters(false)}
          title="Filters"
          variant="sheet"
          size="md"
        >
          <ModalBody className="pb-4">{filterControls(true)}</ModalBody>
          <ModalFooter align="split">
            <Button
              type="button"
              variant="secondary"
              onClick={resetFilters}
              disabled={!isResettable}
            >
              Reset
            </Button>
            <Button type="button" onClick={() => setShowFilters(false)}>
              Done
            </Button>
          </ModalFooter>
        </Modal>
      </div>

      {trendingRailEligible && trendingLoading && (
        <section className="mb-6" aria-busy="true" aria-label="Trending events loading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-dark-text mb-3">Trending</h2>
          </div>
          <TrendingRailSkeleton />
        </section>
      )}
      {/* A failed refetch with cards in hand still shows the rail (it vanished with no service, 2026-10-04). */}
      {trendingRailEligible && !trendingLoading && railEvents.length > 0 && (
        <section className="mb-6" aria-label="Trending events">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-dark-text mb-3">Trending</h2>
          </div>
          <div className="relative">
            <div
              ref={railRef}
              className="trending-rail-scroll overflow-x-auto snap-x snap-mandatory [@media(hover:hover)]:snap-none flex gap-3 pb-2 scrollbar-hide pl-4 pr-4 sm:pl-6 sm:pr-6 scroll-pl-4 sm:scroll-pl-6"
              onScroll={(e) => {
                const el = e.currentTarget;
                const start = el.scrollLeft <= 1,
                  end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
                if (start !== railEnds.start || end !== railEnds.end) setRailEnds({ start, end });
              }}
            >
              {(railLoop ? [...railShown, ...railShown.slice(0, 2)] : railShown).map((event, i) => {
                const copy = i >= railShown.length;
                const myStatus = userRsvpByEvent[event.event_id] ?? null;
                const eventForCard = buildEventForCardFromFeedEvent(event);
                const handleRSVP = async (eventId, status) => {
                  const mockEvent = {
                    stopPropagation: () => {},
                    preventDefault: () => {},
                  };
                  if (status === 'going') {
                    await handleCardGoing(eventId, myStatus, mockEvent, 'trending-card');
                  } else if (status === null && myStatus === 'going') {
                    await handleCardGoing(eventId, myStatus, mockEvent, 'trending-card');
                  }
                };
                return (
                  <div
                    key={copy ? `${event.event_id}-copy` : event.event_id}
                    data-rail-copy={copy && i === railShown.length ? '' : undefined}
                    aria-hidden={copy || undefined}
                    className="snap-start shrink-0 w-[85%] max-w-[640px] [@media(hover:hover)]:w-[320px] cursor-pointer"
                    onClick={(e) => {
                      if (!e.target.closest('button')) {
                        navigate(`/events/${event.event_id}`);
                      }
                    }}
                  >
                    <EventCard
                      event={eventForCard}
                      headliners={railHeadliners[event.event_id] ?? null}
                      variant="hero"
                      userStatus={myStatus}
                      onRSVP={handleRSVP}
                      joinSource="trending-card"
                    />
                  </div>
                );
              })}
            </div>
            {/* A mouse has no swipe: arrows at the rail's ends page through it (desktop browser, 2026-10-23). */}
            {[
              [-1, 'left-2 sm:left-4', 'Previous events', ChevronLeft, railEnds.start],
              [1, 'right-2 sm:right-4', 'More events', ChevronRight, railEnds.end],
            ].map(([dir, side, label, Icon, hidden]) => (
              <div
                key={dir}
                className={`trending-rail-arrow absolute top-1/2 -translate-y-1/2 z-10 ${side} ${hidden ? 'trending-rail-arrow--off' : ''}`}
              >
                <Button
                  variant="icon"
                  size="lg"
                  aria-label={label}
                  onClick={() => {
                    noteTrendingTouch();
                    scrollTrendingBy(dir, true);
                  }}
                >
                  <Icon className="h-6 w-6" aria-hidden />
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* A first-time filter keeps the old list on screen while the new one loads. That wait used to show the yellow
          "Updating results…" banner - the offline/stale warning's look, pushing the page down (operator, 2026-09-29);
          now the list just dims until the results land. The yellow banner is for stale data only. */}
      <div
        className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-opacity duration-150 ${showFeedFilterBanner ? 'opacity-50' : ''}`}
        aria-busy={showFeedFilterBanner || undefined}
      >
        {isInitialFeedLoading ? (
          <EventListSkeleton count={6} />
        ) : isAutoPaginationChasing ? (
          <EventListSkeleton count={4} />
        ) : showFeedExhaustedEmpty ? (
          <p className="text-gray-500 dark:text-dark-muted py-12 text-center">No events found.</p>
        ) : (
          <EventFeedResultsGrid
            events={listShown}
            userRsvpByEvent={userRsvpByEvent}
            hasMore={hasMore}
            loadingMore={loadingMore}
            joinSource="feed-card"
            onEventNavigate={(eventId) => navigate(`/events/${eventId}`)}
            onRSVP={async (eventId, status, myStatus) => {
              const mockEvent = {
                stopPropagation: () => {},
                preventDefault: () => {},
              };
              if (status === 'going' || (status === null && myStatus === 'going')) {
                await handleCardGoing(eventId, myStatus, mockEvent, 'feed-card');
              }
            }}
            lastEventElementRef={lastEventElementRef}
          />
        )}
      </div>

      {/* Going Options Modal */}
      <GoingOptionsModal
        isOpen={active && goingOptionsModal.show}
        onClose={closeGoingOptionsModal}
        onMatchMe={() => handleGoingOption('queue')}
        onCreateGroup={(name) => handleGoingOption('create', name)}
        onSkip={() => handleGoingOption('skip')}
        onJoinExistingGroup={handleJoinExistingGroup}
        eventId={goingOptionsModal.eventId ?? undefined}
        eventName={
          goingOptionsModal.eventId
            ? resolveEventDisplayName(
                events.find((e) => e.event_id === goingOptionsModal.eventId) ??
                  trendingEvents.find((e) => e.event_id === goingOptionsModal.eventId) ??
                  leadFestivals.find((e) => e.event_id === goingOptionsModal.eventId),
              ) || undefined
            : undefined
        }
      />

      {active && festivalSetupModal}

      <RejoinModal
        isOpen={active && rejoinModal.show}
        onClose={() =>
          setRejoinModal({
            show: false,
            group: null,
            eventId: null,
            skippedGroupIds: [],
            remainingCandidates: [],
          })
        }
        onRejoin={handleRejoinGroup}
        onFindDifferent={handleSkipRejoinGroup}
        groupName={rejoinModal.group?.group_name}
      />

      <EditRsvpSheet
        isOpen={active && editRsvp.show}
        onClose={() => setEditRsvp({ show: false, eventId: null, source: null })}
        onNotGoing={() => {
          const { eventId, source } = editRsvp;
          setEditRsvp({ show: false, eventId: null, source: null });
          handleCardGoing(eventId, 'going', { stopPropagation: () => {} }, source, true);
        }}
      />

      {/* Un-RSVP Confirmation Modal */}
      <UnRsvpConfirm
        isOpen={active && confirmUnRSVPModal.show}
        onClose={() =>
          setConfirmUnRSVPModal({ show: false, eventId: null, groups: [], hasQueueEntry: false })
        }
        onConfirm={handleConfirmUnRSVP}
        groups={confirmUnRSVPModal.groups}
        hasQueueEntry={confirmUnRSVPModal.hasQueueEntry}
      />
    </div>
  );
}
